import type { MessageKind } from "../types";

/**
 * How many of a thread's oldest messages fold away behind "Show N earlier
 * messages".
 *
 * The thread keeps its last few messages open, and the question is "last few
 * of what". It used to be the last few of everything, notes included -- so
 * three agents each leaving a note on a ticket ("chased the courier", "rang
 * them", "waiting on stock") folded the entire email chain behind the button,
 * and the next person to open it saw a column of internal chatter and none of
 * what the customer had actually said. Notes are commentary on the
 * conversation; they cannot be allowed to push it out of view.
 *
 * So the count is taken over what was said to or by the customer -- inbound
 * and outbound, whatever the channel -- and the fold sits just before the
 * `keep`th newest of those. Notes on the visible side of that line stay
 * visible, interleaved where they were written; notes on the folded side go
 * with the emails they sat between, because they are as much history as those
 * are. A thread with fewer than `keep` customer-facing messages does not fold
 * at all, however many notes it has: there is nothing long to shorten, and
 * folding the notes alone would hide the very thing the agents wrote down for
 * one another.
 *
 * The answer is a count from the oldest end, so the caller's `slice` and the
 * button's label stay what they were. It includes the notes that fold, since
 * "Show 4 earlier messages" that opened onto six would be a small lie.
 *
 * Messages are expected oldest first, which is how the API returns them.
 */
export function foldedCount(
  messages: readonly { kind: MessageKind }[],
  keep: number,
): number {
  if (keep <= 0) return messages.length;

  let seen = 0;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]!.kind === "note") continue;
    seen += 1;
    if (seen === keep) return index;
  }
  return 0;
}
